/**
 * Utility functions for image optimization across the website
 */

/**
 * Get optimal sizes attribute for responsive images
 * @param type - The type of image placement
 * @returns Properly formatted sizes attribute
 */
export function getImageSizes(type: 'hero' | 'card' | 'gallery' | 'banner' = 'card'): string {
  switch (type) {
    case 'hero':
      return '(max-width: 640px) 100vw, (max-width: 1024px) 95vw, 1536px';
    case 'card':
      return '(max-width: 640px) 95vw, (max-width: 768px) 45vw, (max-width: 1024px) 30vw, 384px';
    case 'gallery':
      return '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';
    case 'banner':
      return '100vw';
    default:
      return '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw';
  }
}

/**
 * Prefetch critical images
 * Programmatically add preload links for critical images
 */
export function prefetchCriticalImages(): void {
  if (typeof window === 'undefined') return;

  // Define critical images based on path
  const path = window.location.pathname;
  let criticalImages: {src: string, type: string}[] = [];

  // Add path-specific critical images
  if (path === '/' || path === '') {
    criticalImages.push({
      src: '/images/giovanni-cordova-studio-mixing-mastering-neve-console-london-uk-2025.jpg',
      type: 'image/jpeg'
    });
  } else if (path.startsWith('/blog/vocal-processing-tips')) {
    criticalImages.push({
      src: '/images/blog/james-herrera-vocal-production-interview-2025-desktop.webp',
      type: 'image/webp'
    });
  }

  // Create preload links
  criticalImages.forEach(image => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    link.type = image.type;
    link.fetchPriority = 'high';
    document.head.appendChild(link);
  });
}

/**
 * Apply proper width and height to prevent CLS
 * @param src The image source
 * @returns The width and height based on image type
 */
export function getImageDimensions(src: string): { width: number, height: number } {
  // Default dimensions
  const defaultDimensions = { width: 1200, height: 800 };
  
  if (!src) return defaultDimensions;
  
  // Check image type by path
  if (src.includes('hero') || src.includes('console')) {
    return { width: 1920, height: 1080 };
  } else if (src.includes('blog')) {
    return { width: 1200, height: 675 };
  } else if (src.includes('gallery')) {
    return { width: 800, height: 800 };
  }
  
  return defaultDimensions;
} 